// Display formatting with the legacy outputs (parity/backoffice/design-system.md §9): `Utils.price` money
// ($1,234.56, -$13.74), report dates ("October 03, 2026", "Saturday, October 10, 2026") and wall-clock times
// ("7:05 pm"). Pure string work over values already computed elsewhere: money arithmetic stays in core (A4) and every
// date or time arrives on the location's clock (LocalDate / LocationTime), so nothing here reads the browser's zone.
import { money, ratio, type Currency, type Money } from '@radish/primitives';
import { isNegative, negate, round } from '@radish/core/money';
import { hundredthsOfHour } from '@radish/core/labor';
import { isSupportedZone, wallClockAt, weekdayOf, type LocalDate } from '@radish/core/time';

const groupThousands = (digits: string): string => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** `Utils.price`: $1,234.56; negative → -$1,234.56. */
export function formatMoney(m: Money): string {
  const negative = isNegative(m);
  const digits = (negative ? negate(m) : m).minor.toString().padStart(3, '0');
  const text = `$${groupThousands(digits.slice(0, -2))}.${digits.slice(-2)}`;
  return negative ? `-${text}` : text;
}

/** Whole dollars, rounded half up as legacy's `Math.round` does ("$35"): the timecards day roll-up of meal premiums. */
export function formatWholeMoney(m: Money): string {
  return `$${groupThousands(round(ratio(m.minor, 100n), 'HALF_UP').toString())}`;
}

/** A report figure in minor units as money in the location's currency. */
export const reportMoney = (minor: bigint, currency: Currency): string => formatMoney(money(minor, currency));

/** A signed adjustment such as the cash rounding, where the sign is the point (legacy `formatSignedPrice`): +$0.02, -$0.03, $0.00. */
export function formatSignedReport(minor: bigint, currency: Currency): string {
  const text = reportMoney(minor, currency);
  return minor > 0n ? `+${text}` : text;
}

/** A deduction row (Refunds, Discounts): always led by a minus, "-$0.00" included, as legacy prints it. */
export function formatDeduction(minor: bigint, currency: Currency): string {
  const m = money(minor, currency);
  return `-${formatMoney(isNegative(m) ? negate(m) : m)}`;
}

/** Thousandths of a unit (core qtyMilli) as the legacy Qty column: whole numbers plain, fractions to three places. */
export function formatQty(milli: bigint): string {
  // A net figure below zero (a Favorite Items row whose returns outweigh its sales) reads with its sign, as legacy's does.
  if (milli < 0n) return `-${formatQty(-milli)}`;
  const digits = milli.toString().padStart(4, '0');
  const whole = digits.slice(0, -3);
  const fraction = digits.slice(-3).replace(/0+$/, '');
  return fraction === '' ? groupThousands(whole) : `${groupThousands(whole)}.${fraction}`;
}

/** Seconds as payroll hours with two decimals ("0.01 hrs"), rounded where core rounds them. */
export function formatHours(seconds: bigint): string {
  const hundredths = hundredthsOfHour(Number(seconds)).toString().padStart(3, '0');
  return `${hundredths.slice(0, -2)}.${hundredths.slice(-2)}`;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

const partsOf = (date: string): { readonly year: string; readonly month: string; readonly day: string } => ({
  year: date.slice(0, 4), month: MONTHS[Number(date.slice(5, 7)) - 1] ?? '', day: date.slice(8, 10),
});

/**
 * "October 03, 2026", the day padded: legacy `MMMM DD, YYYY` and PHP `F d, Y` (Plans & Billing's Next Billing Date,
 * `auto_pay_on`). Where legacy prints the day unpadded (`MMMM D, YYYY` or PHP `F j, Y`: the First Billing Date, the
 * Adjustments PDFs), use `formatLongDateUnpadded`.
 */
export function formatLongDate(date: string): string {
  const p = partsOf(date);
  return `${p.month} ${p.day}, ${p.year}`;
}

/** "October 3, 2026", the day unpadded (legacy's Blade PDFs, PHP `F j, Y`). */
export function formatLongDateUnpadded(date: string): string {
  const p = partsOf(date);
  return `${p.month} ${String(Number(p.day))}, ${p.year}`;
}

/** "Oct 9" (legacy `MMM D`). */
export function formatShortMonthDay(date: string): string {
  return `${partsOf(date).month.slice(0, 3)} ${String(Number(date.slice(8, 10)))}`;
}

/** "Friday, October 09, 2026", the day padded (legacy's timecard and payroll PDF headings, `dddd, MMMM DD, YYYY`). */
export function formatDayHeading(date: string): string {
  return `${WEEKDAYS[weekdayOf(date as LocalDate) - 1] ?? ''}, ${formatLongDate(date)}`;
}

/** "Friday, October 9, 2026", the day unpadded (legacy `formatBusinessDay`: the Timecards list's day groups, B2097). */
export function formatBusinessDayHeading(date: string): string {
  return `${WEEKDAYS[weekdayOf(date as LocalDate) - 1] ?? ''}, ${formatLongDateUnpadded(date)}`;
}

/** "Oct 08, 2026" (legacy `MMM DD, YYYY`: the Dashboard's welcome line). */
export function formatShortDate(date: string): string {
  const p = partsOf(date);
  return `${p.month.slice(0, 3)} ${p.day}, ${p.year}`;
}

/** "Thursday, Oct 08, 2026" (legacy `dddd, MMM DD, YYYY`: the EOD Report list and PDF). */
export function formatWeekdayShortDate(date: string): string {
  return `${WEEKDAYS[weekdayOf(date as LocalDate) - 1] ?? ''}, ${formatShortDate(date)}`;
}

/** "10-10-2026" (the timecard editor's Select Date). */
export const formatDashedDate = (date: string): string => `${date.slice(5, 7)}-${date.slice(8, 10)}-${date.slice(0, 4)}`;

/** The report heading: one date, or "start - end". */
export function formatRange(from: string, to: string): string {
  return from === to ? formatDayHeading(from) : `${formatLongDate(from)} - ${formatLongDate(to)}`;
}

/** "19:05:00" → "7:05 pm" (legacy `h:mm a`). */
export function formatClock(time: string): string {
  const hour = Number(time.slice(0, 2));
  const minute = time.slice(3, 5);
  const suffix = hour < 12 ? 'am' : 'pm';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(h12)}:${minute} ${suffix}`;
}

/** "2026-10-11" at minute 650 → "10/11/2026 10:50 am" (legacy `MM/DD/YYYY h:mm a`, the audit reports' Date & Time). */
export function formatDateMinute(date: string, minute: number): string {
  const clock = `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}:00`;
  return `${date.slice(5, 7)}/${date.slice(8, 10)}/${date.slice(0, 4)} ${formatClock(clock)}`;
}

/** "2026-11-01" at minute 1215 → "11/01/2026 08:15 pm" (legacy `Utils.dateTinyTimeWithoutTimezone`, `MM/DD/YYYY hh:mm a`). */
export function formatDateMinutePadded(date: string, minute: number): string {
  const clock = `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}:00`;
  return `${date.slice(5, 7)}/${date.slice(8, 10)}/${date.slice(0, 4)} ${formatClockPadded(clock)}`;
}

/** An instant on the location's clock as "10/10/2026 4:51 pm" (legacy `Utils.dateTinyTime`, `MM/DD/YYYY h:mm a`). */
export function formatInstant(at: string, zone: string): string {
  if (!isSupportedZone(zone)) return at;
  const clock = wallClockAt(Date.parse(at), zone);
  return formatDateMinute(clock.date, Number(clock.time.slice(0, 2)) * 60 + Number(clock.time.slice(3, 5)));
}

/** "19:05:00" → "07:05 pm" (the legacy time inputs, zero-padded). */
export function formatClockPadded(time: string): string {
  const text = formatClock(time);
  return text.length === 7 ? `0${text}` : text;
}

/** "07:05 pm" / "7:05pm" / "19:05" → "19:05", or null when it is not a time. */
export function parseClock(text: string): string | null {
  const match = /^\s*(\d{1,2}):(\d{2})\s*(am|pm)?\s*$/i.exec(text);
  if (match === null) return null;
  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const suffix = match[3]?.toLowerCase();
  if (minute > 59) return null;
  if (suffix === undefined) {
    if (hour > 23) return null;
  } else {
    if (hour < 1 || hour > 12) return null;
    hour = (hour % 12) + (suffix === 'pm' ? 12 : 0);
  }
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * Legacy library/timezone.ts formatTimezoneLabel: an IANA zone's underscores read as spaces ("America/Los Angeles"). For
 * people only: a zone passed to a formatter or the API keeps its name.
 */
export const formatTimezoneLabel = (timezone: string): string => timezone.replace(/_/g, ' ');

/** "04:00" → "4 AM" (the business-day line under report headings). */
export function formatHourShort(time: string): string {
  const hour = Number(time.slice(0, 2));
  const minute = time.slice(3, 5);
  const suffix = hour < 12 ? 'AM' : 'PM';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return minute === '00' ? `${String(h12)} ${suffix}` : `${String(h12)}:${minute} ${suffix}`;
}

/** The legacy PhoneInput mask, "(###) ###-####", over the digits typed so far. */
export function maskPhone(digits: string): string {
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}
